import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Shield, Building2, Users, BarChart3, DollarSign, MessageSquare, ChevronRight } from 'lucide-react';

export default function AdminPanel() {
  const { api, user } = useAuth();
  const [stats, setStats] = useState(null);
  const [clinics, setClinics] = useState([]);
  const [selectedClinic, setSelectedClinic] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function load() {
      try {
        const [sRes, cRes] = await Promise.all([
          api().get('/api/admin/stats'),
          api().get('/api/admin/clinics'),
        ]);
        setStats(sRes.data);
        setClinics(cRes.data.clinics || []);
      } catch (e) { console.error(e); }
      setLoading(false);
    }
    if (user?.role === 'SUPER_ADMIN') load();
    else setLoading(false);
  }, [api, user]);

  const loadClinic = async (id) => {
    try {
      const { data } = await api().get(`/api/admin/clinics/${id}`);
      setSelectedClinic(data);
    } catch (e) { console.error(e); }
  };

  const updateClinicTier = async (clinicId, tier) => {
    try {
      await api().patch(`/api/admin/clinics/${clinicId}`, { subscription_tier: tier });
      loadClinic(clinicId);
      const { data } = await api().get('/api/admin/clinics');
      setClinics(data.clinics || []);
    } catch (e) { console.error(e); }
  };

  if (user?.role !== 'SUPER_ADMIN') {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <Shield size={40} className="mx-auto text-slate-300 mb-4" />
          <p className="text-lg font-semibold text-slate-900">Access Denied</p>
          <p className="text-sm text-slate-500 mt-1">This page is restricted to Super Admins.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="w-6 h-6 border-2 border-slate-900 border-t-transparent rounded-full animate-spin" /></div>;
  }

  const TIER_LABELS = {
    recovery_core: 'Recovery Core',
    growth_accelerator: 'Growth Accelerator',
    premium_multi_location: 'Premium Multi-Location',
  };

  return (
    <div data-testid="admin-panel" className="space-y-6">
      <div className="flex items-center gap-3">
        <Shield size={24} className="text-slate-900" />
        <div>
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Super Admin Panel</h1>
          <p className="text-sm text-slate-500">Digital Reign internal management</p>
        </div>
      </div>

      {/* Platform Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { label: 'Total Clinics', value: stats.total_clinics, icon: Building2, bg: 'bg-blue-50', color: 'text-blue-500' },
            { label: 'Total Users', value: stats.total_users, icon: Users, bg: 'bg-violet-50', color: 'text-violet-500' },
            { label: 'Total Leads', value: stats.total_leads, icon: BarChart3, bg: 'bg-emerald-50', color: 'text-emerald-500' },
            { label: 'MRR', value: `$${(stats.mrr || 0).toLocaleString()}`, icon: DollarSign, bg: 'bg-slate-100', color: 'text-slate-900' },
            { label: 'Total Patients', value: stats.total_patients, icon: Users, bg: 'bg-amber-50', color: 'text-amber-500' },
            { label: 'Total SMS', value: stats.total_sms, icon: MessageSquare, bg: 'bg-sky-50', color: 'text-sky-500' },
          ].map(s => (
            <div key={s.label} className="bg-white rounded-xl border border-slate-200 p-5">
              <div className={`w-9 h-9 ${s.bg} rounded-lg flex items-center justify-center mb-3`}>
                <s.icon size={18} className={s.color} />
              </div>
              <p className="text-2xl font-bold text-slate-900 tracking-tight">{s.value}</p>
              <p className="text-xs text-slate-500 mt-1">{s.label}</p>
            </div>
          ))}
        </div>
      )}

      {/* Tier Breakdown */}
      {stats?.tier_breakdown && (
        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <h3 className="text-sm font-semibold text-slate-900 mb-3">Plan Distribution</h3>
          <div className="flex gap-4">
            {Object.entries(stats.tier_breakdown).map(([tier, count]) => (
              <div key={tier} className="bg-slate-50 rounded-lg px-4 py-3 flex-1">
                <p className="text-xs text-slate-500">{TIER_LABELS[tier] || tier}</p>
                <p className="text-xl font-bold text-slate-900 mt-1">{count}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Clinic List */}
        <div className="lg:col-span-1 bg-white rounded-xl border border-slate-200 overflow-hidden">
          <div className="p-4 border-b border-slate-200">
            <h3 className="text-sm font-semibold text-slate-900">All Clinics ({clinics.length})</h3>
          </div>
          <div className="max-h-[500px] overflow-y-auto">
            {clinics.map(c => (
              <button
                key={c.id}
                data-testid={`admin-clinic-${c.id}`}
                onClick={() => loadClinic(c.id)}
                className={`w-full text-left px-4 py-3 border-b border-slate-100 hover:bg-slate-50 transition-colors flex items-center gap-3 ${
                  selectedClinic?.id === c.id ? 'bg-slate-50' : ''
                }`}
              >
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-slate-900 truncate">{c.clinic_name}</p>
                  <p className="text-xs text-slate-500">{c.user_count} users, {c.lead_count} leads</p>
                </div>
                <ChevronRight size={14} className="text-slate-300 shrink-0" />
              </button>
            ))}
          </div>
        </div>

        {/* Clinic Detail */}
        <div className="lg:col-span-2">
          {!selectedClinic ? (
            <div className="bg-white rounded-xl border border-slate-200 p-12 text-center text-slate-400">
              <Building2 size={40} className="mx-auto mb-3 text-slate-200" />
              <p className="text-sm">Select a clinic to view details</p>
            </div>
          ) : (
            <div className="bg-white rounded-xl border border-slate-200 p-6 space-y-5">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-lg font-bold text-slate-900">{selectedClinic.clinic_name}</h2>
                  <p className="text-sm text-slate-500">{selectedClinic.email}</p>
                </div>
                <span className="text-xs font-semibold bg-slate-100 text-slate-700 px-3 py-1 rounded-full">
                  {TIER_LABELS[selectedClinic.subscription_tier] || selectedClinic.subscription_tier}
                </span>
              </div>

              <div className="grid grid-cols-3 gap-3">
                <div className="bg-slate-50 rounded-lg p-3">
                  <p className="text-xs text-slate-500">Leads</p>
                  <p className="text-lg font-bold text-slate-900">{selectedClinic.lead_count}</p>
                </div>
                <div className="bg-slate-50 rounded-lg p-3">
                  <p className="text-xs text-slate-500">Patients</p>
                  <p className="text-lg font-bold text-slate-900">{selectedClinic.patient_count}</p>
                </div>
                <div className="bg-slate-50 rounded-lg p-3">
                  <p className="text-xs text-slate-500">SMS Sent</p>
                  <p className="text-lg font-bold text-slate-900">{selectedClinic.sms_count}</p>
                </div>
              </div>

              {/* Users */}
              <div>
                <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Team Members</h4>
                <div className="space-y-2">
                  {selectedClinic.users?.map(u => (
                    <div key={u.id} className="flex items-center gap-3 p-2 bg-slate-50 rounded-lg">
                      <div className="w-7 h-7 rounded-full bg-slate-200 flex items-center justify-center text-xs font-semibold text-slate-600">
                        {u.name?.[0]?.toUpperCase() || '?'}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-slate-900 truncate">{u.name}</p>
                        <p className="text-xs text-slate-500">{u.email}</p>
                      </div>
                      <span className="text-[10px] font-semibold bg-slate-200 text-slate-600 px-2 py-0.5 rounded-full">
                        {u.role?.replace(/_/g, ' ')}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Change Plan */}
              <div>
                <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Change Plan</h4>
                <div className="flex gap-2">
                  {Object.entries(TIER_LABELS).map(([tier, label]) => (
                    <button
                      key={tier}
                      data-testid={`admin-set-tier-${tier}`}
                      onClick={() => updateClinicTier(selectedClinic.id, tier)}
                      disabled={selectedClinic.subscription_tier === tier}
                      className={`text-xs font-medium px-3 py-1.5 rounded-lg transition-all ${
                        selectedClinic.subscription_tier === tier
                          ? 'bg-slate-900 text-white'
                          : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                      } disabled:cursor-default`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Locations */}
              {selectedClinic.locations?.length > 0 && (
                <div>
                  <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Locations</h4>
                  <div className="space-y-2">
                    {selectedClinic.locations.map(l => (
                      <div key={l.id} className="text-sm p-2 bg-slate-50 rounded-lg">
                        <p className="font-medium text-slate-900">{l.location_name}</p>
                        {l.address && <p className="text-xs text-slate-500">{l.address}</p>}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
